import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getCourses } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { BookOpen, Users, Video, CreditCard, ChevronRight, Play } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_lvc-live-classes/artifacts/op54e7uq_WhatsApp%20Image%202026-04-08%20at%2013.06.26.jpeg";

export default function Landing() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const data = await getCourses();
        setCourses(data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      if (user.role === 'admin') navigate('/admin');
      else if (user.role === 'teacher') navigate('/teacher');
      else navigate('/student');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-[#FDFBF7]/80 backdrop-blur-md border-b border-[#E5DFD3]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="LVC Misiones" className="h-12 w-12 rounded-full object-cover" />
            <span className="font-heading text-xl font-semibold text-[#8B1538]">LVC Misiones</span>
          </Link>
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <Button 
                data-testid="dashboard-btn"
                onClick={handleGetStarted}
                className="bg-[#8B1538] hover:bg-[#70102B] text-white"
              >
                Ir al Panel
              </Button>
            ) : (
              <>
                <Link to="/login">
                  <Button data-testid="login-btn" variant="ghost" className="text-[#4A4A4A] hover:text-[#1A1A1A] hover:bg-[#F5F2EA]">
                    Iniciar Sesión
                  </Button>
                </Link>
                <Link to="/register">
                  <Button data-testid="register-btn" className="bg-[#8B1538] hover:bg-[#70102B] text-white">
                    Registrarse
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 px-6 overflow-hidden">
        <div className="absolute inset-0 hero-bg opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#FDFBF7] via-[#FDFBF7]/80 to-transparent" />
        <div className="relative max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#8B1538]">
              Educación Misionera Online
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-[#1A1A1A]">
              La Voz de Cristo<br />
              <span className="text-[#8B1538]">Para Las Naciones</span>
            </h1>
            <p className="text-lg text-[#4A4A4A] leading-relaxed max-w-xl">
              Formación misionera de excelencia con clases en vivo, materiales de estudio 
              y una comunidad comprometida con llevar el evangelio al mundo.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                data-testid="get-started-btn"
                onClick={handleGetStarted}
                size="lg"
                className="bg-[#8B1538] hover:bg-[#70102B] text-white px-8 btn-press"
              >
                Comenzar Ahora
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                data-testid="view-courses-btn"
                variant="outline"
                size="lg"
                onClick={() => document.getElementById('courses').scrollIntoView({ behavior: 'smooth' })}
                className="border-[#8B1538] text-[#8B1538] hover:bg-[#8B1538]/5"
              >
                Ver Cursos
              </Button>
            </div>
          </div>
          <div className="hidden md:block">
            <img 
              src="https://images.pexels.com/photos/5212687/pexels-photo-5212687.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Estudiantes colaborando"
              className="rounded-lg shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-[#F5F2EA]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#8B1538]">
              Por qué elegirnos
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1A1A] mt-4">
              Una Experiencia de Aprendizaje Completa
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Video, title: "Clases en Vivo", desc: "Interactúa en tiempo real con profesores expertos" },
              { icon: BookOpen, title: "Material de Estudio", desc: "Documentos y recursos descargables" },
              { icon: Users, title: "Comunidad", desc: "Conecta con otros estudiantes misioneros" },
              { icon: CreditCard, title: "Pagos Flexibles", desc: "Suscripción mensual o anual" }
            ].map((feature, i) => (
              <Card key={i} className="bg-white border border-[#E5DFD3] shadow-sm course-card">
                <CardContent className="pt-8 pb-6 px-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#8B1538]/10 flex items-center justify-center">
                    <feature.icon className="h-7 w-7 text-[#8B1538]" />
                  </div>
                  <h3 className="font-heading text-xl font-medium text-[#1A1A1A] mb-2">{feature.title}</h3>
                  <p className="text-sm text-[#4A4A4A]">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section id="courses" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#8B1538]">
              Nuestros Cursos
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1A1A] mt-4">
              Formación Misionera de Excelencia
            </h2>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : courses.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {courses.map((course) => (
                <Card key={course.id} data-testid={`course-card-${course.id}`} className={`bg-white border shadow-sm course-card overflow-hidden ${course.is_featured ? 'border-[#8B1538] border-2' : 'border-[#E5DFD3]'}`}>
                  {course.is_featured && (
                    <div className="bg-[#8B1538] text-white text-center py-2 text-sm font-medium">
                      Curso Principal
                    </div>
                  )}
                  <div className="h-48 bg-gradient-to-br from-[#8B1538] to-[#70102B] flex items-center justify-center">
                    {course.thumbnail_url ? (
                      <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center text-white p-4">
                        <BookOpen className="h-12 w-12 mx-auto text-white/50 mb-2" />
                        {course.duration_months && (
                          <p className="text-white/70 text-sm">{course.duration_months} meses</p>
                        )}
                      </div>
                    )}
                  </div>
                  <CardHeader>
                    <CardTitle className="font-heading text-xl text-[#1A1A1A]">{course.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-[#4A4A4A] line-clamp-2">{course.description?.split('\n')[0]}</p>
                    <div className="mt-4 flex items-center gap-2 text-sm text-[#737373]">
                      <Users className="h-4 w-4" />
                      <span>{course.enrolled_count} estudiantes</span>
                      {course.frequency && (
                        <>
                          <span className="mx-1">•</span>
                          <span>{course.frequency}</span>
                        </>
                      )}
                    </div>
                  </CardContent>
                  <CardFooter className="border-t border-[#E5DFD3] bg-[#FDFBF7] flex justify-between items-center">
                    <div>
                      <span className="text-xs text-[#737373]">Mensualidad</span>
                      <p className="font-semibold text-[#8B1538]">${course.price_monthly.toLocaleString()}</p>
                    </div>
                    <Button 
                      data-testid={`enroll-btn-${course.id}`}
                      onClick={() => navigate(isAuthenticated ? `/student/course/${course.id}` : '/login')}
                      size="sm"
                      className="bg-[#8B1538] hover:bg-[#70102B] text-white"
                    >
                      Ver Detalles
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <BookOpen className="h-16 w-16 mx-auto text-[#D9B48F] mb-4" />
              <p className="text-[#4A4A4A]">Próximamente nuevos cursos disponibles</p>
            </div>
          )}
          
          {courses.length > 0 && (
            <div className="text-center mt-12">
              <Button 
                data-testid="view-all-courses-btn"
                variant="outline"
                onClick={() => navigate(isAuthenticated ? '/student' : '/login')}
                className="border-[#8B1538] text-[#8B1538] hover:bg-[#8B1538]/5"
              >
                Ver Todos los Cursos
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#8B1538]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white mb-6">
            Comienza Tu Formación Misionera Hoy
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Únete a nuestra comunidad y recibe formación de calidad para cumplir el llamado misionero.
          </p>
          <Button 
            data-testid="cta-register-btn"
            onClick={() => navigate('/register')}
            size="lg"
            className="bg-white text-[#8B1538] hover:bg-[#F5F2EA] px-8"
          >
            Registrarse Gratis
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-[#1A1A1A] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="LVC Misiones" className="h-10 w-10 rounded-full object-cover" />
              <div>
                <p className="font-heading text-lg">La Voz de Cristo Para Las Naciones</p>
                <p className="text-sm text-white/60">Formación Misionera Online</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/manual" className="text-sm text-white/60 hover:text-white transition-colors">Manual de Usuario</Link>
              <p className="text-sm text-white/60">
                © {new Date().getFullYear()} LVC Misiones. Todos los derechos reservados.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
